import React, { useEffect, useRef, useCallback } from 'react';
import './TicketListModal.css';

/**
 * The dialog shell behind every drill-down: backdrop, header, focus handling.
 *
 * Closes on Escape and on a click outside, returns focus to whatever opened it,
 * and traps Tab inside while open, so it is usable without a mouse. The table
 * itself is the caller's business — see TicketListModal and WorkOrderListModal.
 */
export default function ListModal({ title, subtitle, count, noun = 'record', onClose, children }) {
  const panelRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    const panel = panelRef.current;
    if (panel) {
      const first = panel.querySelector('button, a, [tabindex]');
      (first || panel).focus();
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (openerRef.current && openerRef.current.focus) openerRef.current.focus();
    };
  }, []);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = [...panel.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')]
      .filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, [onClose]);

  const plural = count === 1 ? noun : `${noun}s`;

  return (
    <div
      className="tlm-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={onKeyDown}
    >
      <div
        className="tlm-panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="tlm-header">
          <div>
            <h2 className="tlm-title">{title}</h2>
            <p className="tlm-subtitle">
              {count === null || count === undefined ? 'Loading…' : `${count} ${plural}`}
              {subtitle ? ` · ${subtitle}` : ''}
            </p>
          </div>
          <button className="tlm-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="tlm-body">{children}</div>
      </div>
    </div>
  );
}
